// Polish Phase 1 (AUDIO-02/03/07) — React binding for the audio engine.
//
// Mounted ONCE at the app root (RootApp). It:
//   - mirrors the $sfxGain store (mute + volume) onto the engine's SFX bus,
//   - sets the single static take-two audio profile (WS0b/ARCA-87 removed the
//     per-theme swap — there is one canonical profile),
//   - resumes the AudioContext on the first user gesture (autoplay unlock),
//     then detaches the one-shot listeners.
//
// Keeping all of this in one root hook means call-sites just `playSfx(cue)`
// with no provider plumbing — the engine is a singleton bound here.

import { useEffect } from 'react'
import { $sfxGain } from '../../stores/audio'
import { audioEngine, playSfx } from './engine'
import { DEFAULT_AUDIO_PROFILE } from './theme-audio'

export { playSfx }

/** Mount once at the app root. Binds stores → engine and wires first-gesture
 * unlock. */
export function useAudioRoot(): void {
  useEffect(() => {
    // Bind SFX gain (mute + volume). subscribe() fires immediately with the
    // current value, so the engine's targetGain is correct before unlock.
    const unbindGain = $sfxGain.subscribe((gain) => {
      audioEngine.setSfxGain(gain)
    })
    // Single static take-two audio profile (no theme swap).
    audioEngine.setProfile(DEFAULT_AUDIO_PROFILE)

    // First-gesture unlock (AUDIO-02). One listener per gesture type; the
    // first to fire unlocks and removes them all.
    if (typeof window !== 'undefined') {
      const events: ReadonlyArray<keyof WindowEventMap> = [
        'pointerdown',
        'keydown',
        'touchstart',
      ]
      const onGesture = (): void => {
        audioEngine.unlock()
        for (const ev of events) window.removeEventListener(ev, onGesture)
      }
      for (const ev of events) {
        window.addEventListener(ev, onGesture, { passive: true })
      }
      return () => {
        for (const ev of events) window.removeEventListener(ev, onGesture)
        unbindGain()
      }
    }
    return () => {
      unbindGain()
    }
  }, [])
}
